import React from 'react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { slugify } from '../../../../../../Shared/Frontend/utils/slugify'
import { dataLayerServices } from '../../../../seo/datalayer'
import type { ActionProps } from '../../modernBlocks.state'
import { SharedBlockLayout } from '../shared/sharedBlock.layout'
import type { DownloadBlockClassNames, DownloadItem } from './downloadBlock.state'
import { htmlDecode } from '../../../utils/html'

export const downloadBlockClasses: DownloadBlockClassNames = {
  // DEFAULTS
  block: 'block block--list block--download',
  inview: 'inview',
  inner: 'block__inner ls-container ls-gutter ls-spacing--s',
  header: 'block__header',
  label: 'block__label text--s',
  title: 'block__title title--m',
  intro: 'block__intro text--l',
  content: 'block__content',
  footer: 'block__footer ls-clickables',
  clickableWrapper: 'block__action ls-clickable',
  clickablePrimary: 'block__button button button--primary',
  clickableSecondary: 'block__button button button--tertiary',

  // BLOCK SPECIFIC
  list: 'list',
  item: 'list__item',
  itemClickable: 'list__link',
  itemText: 'list__text',
  itemTitle: 'list__title',
  itemSubTitle: 'list__description text--s',
  itemIcon: 'list__icon',
}

export const DownloadBlockLayout = {
  Inner: (props: { children?: React.ReactNode; amountNested: number }): JSX.Element => {
    const { amountNested, children } = props
    return (
      <div className={getClassNameModern(downloadBlockClasses, 'inner', 'mw-container-s mx-auto px-gutter')}>
        {children}
      </div>
    )
  },
  Content: (props: {
    label?: string,
    title?: string,
    intro?: string,
    items?: DownloadItem[]
  } & ActionProps) => {
    const {
      label,
      title,
      intro,
      items,
      showPrimary,
      showSecondary,
      primaryAction,
      secondaryAction,
    } = props
    return (
      <>
        {(label || title || intro) && (
          <header className={getClassNameModern(downloadBlockClasses, 'header', '')}>
            {label &&
              <p className={getClassNameModern(downloadBlockClasses, 'label', 'fs-6 text-primary text-uppercase')}>{label}</p>
            }
            {title &&
              <h2 id={slugify(htmlDecode(title))} className={getClassNameModern(downloadBlockClasses, 'title', 'h2 mb-3')}>{title}</h2>
            }
            {intro &&
              <p
                className={getClassNameModern(downloadBlockClasses, 'intro', 'text--styled mb-3')}
                dangerouslySetInnerHTML={{ __html: intro }}
              />
            }
          </header>
        )}

        <div className={getClassNameModern(downloadBlockClasses, 'content', '')}>
          {(items && items.length > 0) &&
            <ul className={getClassNameModern(downloadBlockClasses, 'list', 'list-group mt-5')}>
              {items.map((link) => {
                const { fileName, fileUrl, title, key, text } = link
                return (
                  fileUrl && (
                    <li
                      className={getClassNameModern(downloadBlockClasses, 'item', 'list-group-item')}
                      key={`download-${slugify(text || '')}-${key}`}
                    >
                      <a
                        onClick={() => dataLayerServices.downloadDataLayer('download', fileName)}
                        id={`download-item-${key}`}
                        download={true}
                        target='_blank'
                        rel='noopener noreferrer'
                        href={fileUrl}
                        className={getClassNameModern(
                          downloadBlockClasses,
                          'itemClickable',
                          'd-flex align-items-baseline py-2 text-decoration-none'
                        )}
                      >
                        <SharedBlockLayout.SVGIcon iconId='#icon--download' width='24px' height='24px' className={getClassNameModern(downloadBlockClasses, 'itemIcon', 'me-3')} />

                        <div className={getClassNameModern(downloadBlockClasses, 'itemText', '')}>
                          {(title || fileName) && (
                            <p className={getClassNameModern(downloadBlockClasses, 'itemTitle', '')}>
                              {title || fileName}
                            </p>
                          )}

                          {text && (
                            <p className={getClassNameModern(downloadBlockClasses, 'itemSubTitle', 'small')}>
                              {text}
                            </p>
                          )}
                        </div>
                      </a>
                    </li>
                  )
                )
              })}
            </ul>
          }

          {(showPrimary || showSecondary) && (
            <footer className={getClassNameModern(downloadBlockClasses, 'footer', 'pt-5')}>
              {showPrimary && primaryAction && primaryAction.title && primaryAction.url && (
                <div className={getClassNameModern(downloadBlockClasses, 'clickableWrapper', 'd-block d-sm-inline-block')}>
                  <SharedBlockLayout.Clickable
                    id='downloadPrimaryAction'
                    type='button'
                    link={primaryAction}
                    classNames={getClassNameModern(downloadBlockClasses, 'clickablePrimary', 'btn btn-primary')}
                  />
                </div>
              )}
              {showSecondary && secondaryAction && secondaryAction.title && secondaryAction.url && (
                <div
                  className={getClassNameModern(
                    downloadBlockClasses,
                    'clickableWrapper',
                    'd-block d-sm-inline-block ms-sm-3 mt-3 mt-sm-0'
                  )}
                >
                  <SharedBlockLayout.Clickable
                    id='downloadSecondaryAction'
                    type='button'
                    link={secondaryAction}
                    classNames={getClassNameModern(downloadBlockClasses, 'clickableSecondary', 'btn btn-outline-primary')}
                  />
                </div>
              )}
            </footer>
          )}
        </div>
      </>
    )
  }
}
